import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  Wrench,
  Hammer,
  Paintbrush,
  Droplets,
  Zap,
  Layers,
  ArrowRight,
  CheckCircle2,
  Home as HomeIcon,
  Key,
  Building,
  Clock,
  Shield,
  Users,
  Calendar,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import {
  companyInfo,
  specialistBrands,
  coreServices,
  landlordServices,
  howItWorks,
  whyChooseUs,
  serviceAreas,
  customerTypes
} from '../data/mock';

const iconMap = {
  Wrench, Hammer, Paintbrush, Droplets, Zap, Layers, Home: HomeIcon, Key, Building,
  Clock, Shield, Users, Calendar, Tool: Wrench
};

// Master brand logo
const MASTER_LOGO_URL = "https://customer-assets-lqy194kg.emergentagent.net/job_f637d394-69ad-4c84-81b1-378d0bfe728e/artifacts/kpay4epr_image.png";

// ============ HERO SECTION ============
const HeroSection = () => (
  <section className="relative pt-10 pb-8 sm:pt-20 sm:pb-14 lg:pt-32 lg:pb-24 bg-gradient-to-b from-brand-navy via-slate-900 to-brand-navy/90 overflow-hidden">
    {/* Subtle pattern overlay */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute inset-0" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
    </div>
    
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl">
        <h1 className="text-[30px] leading-[1.15] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white sm:leading-tight mb-3 sm:mb-6">
          Property Maintenance for{' '}
          <span className="text-brand-yellow">Landlords</span>,{' '}
          <span className="text-brand-yellow">Letting Agents</span>{' '}
          <span className="text-white">&</span>{' '}
          <span className="text-brand-yellow">Property Professionals</span>
        </h1>
        
        <p className="text-[16px] leading-relaxed sm:text-lg lg:text-xl text-slate-300 mb-4 sm:mb-6 max-w-2xl">
          One point of contact for multi-trade repairs, maintenance and refurbishment across Greater Manchester, Cheshire and surrounding areas.
        </p>

        <p className="text-[15px] leading-relaxed sm:text-base lg:text-lg text-slate-400 mb-5 sm:mb-8 max-w-2xl">
          From individual repairs to complete property refurbishments — for landlords, letting agents, property managers and homeowners alike — we coordinate the work and provide one clear point of contact throughout.
        </p>
        
        <div className="flex flex-col gap-2.5 sm:flex-row sm:gap-4 mb-5 sm:mb-10">
          <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold text-[16px] sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto" asChild data-testid="hero-cta-quote">
            <a href="#contact">
              Request a Quote
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy font-semibold text-[16px] sm:text-lg px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto" asChild data-testid="hero-cta-call">
            <a href={`tel:${companyInfo.phone}`}>
              <Phone className="w-5 h-5 mr-2" />
              Call {companyInfo.phone}
            </a>
          </Button>
        </div>
        
        {/* Trust signals - compact on mobile, row on desktop */}
        <div className="grid grid-cols-1 gap-1.5 sm:flex sm:flex-row sm:flex-wrap sm:gap-x-8 sm:gap-y-3 text-[15px] sm:text-base lg:text-[17px]">
          <div className="flex items-center gap-2 sm:gap-2.5 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow flex-shrink-0" />
            <span className="text-white sm:text-slate-200 font-medium sm:font-normal">{companyInfo.insurance.publicLiability}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-2.5 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow flex-shrink-0" />
            <span className="text-white sm:text-slate-200 font-medium sm:font-normal">{companyInfo.insurance.employersLiability}</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-2.5 bg-white/8 sm:bg-transparent px-2.5 py-1.5 sm:p-0 rounded">
            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-brand-yellow flex-shrink-0" />
            <span className="text-white sm:text-slate-200 font-medium sm:font-normal">{companyInfo.guarantee}</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============ CUSTOMER PATHWAY SECTION ============
const CustomerPathwaySection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 sm:mb-14">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          How Can We Help?
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate max-w-2xl mx-auto leading-relaxed">
          Select your property type to see our relevant services
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6 lg:gap-10">
        {/* Landlords & Letting Agents Card */}
        <Link
          to="/landlord-services"
          className="group block bg-brand-grey border-2 border-transparent hover:border-brand-yellow rounded-2xl p-7 sm:p-9 lg:p-12 transition-all hover:shadow-xl"
          data-testid="pathway-landlords"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 lg:w-20 lg:h-20 bg-brand-navy rounded-xl flex items-center justify-center group-hover:bg-brand-yellow transition-colors flex-shrink-0">
              <Key className="w-8 h-8 lg:w-9 lg:h-9 text-brand-yellow group-hover:text-brand-navy transition-colors" />
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-[26px] font-bold text-brand-navy">Landlords & Letting Agents</h3>
          </div>
          <ul className="space-y-3.5 mb-7 text-brand-slate">
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Reactive maintenance</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Tenant-reported repairs</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> End-of-tenancy & void works</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Multi-trade property refurbishment</li>
          </ul>
          <span className="inline-flex items-center text-brand-navy font-bold text-base lg:text-lg group-hover:text-brand-yellow transition-colors">
            Landlord & Agent Services <ChevronRight className="w-5 h-5 ml-1" />
          </span>
        </Link>
        
        {/* Homeowners Card */}
        <Link
          to="/homeowner-services"
          className="group block bg-brand-grey border-2 border-transparent hover:border-brand-yellow rounded-2xl p-7 sm:p-9 lg:p-12 transition-all hover:shadow-xl"
          data-testid="pathway-homeowners"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 lg:w-20 lg:h-20 bg-brand-navy rounded-xl flex items-center justify-center group-hover:bg-brand-yellow transition-colors flex-shrink-0">
              <HomeIcon className="w-8 h-8 lg:w-9 lg:h-9 text-brand-yellow group-hover:text-brand-navy transition-colors" />
            </div>
            <h3 className="text-xl sm:text-2xl lg:text-[26px] font-bold text-brand-navy">Homeowners</h3>
          </div>
          <ul className="space-y-3.5 mb-7 text-brand-slate">
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Painting &amp; decorating</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Carpentry, joinery, doors, skirting &amp; architraves</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Plaster repairs, shelving &amp; storage</li>
            <li className="flex items-center gap-3 text-[15px] sm:text-base lg:text-[17px]"><CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" /> Minor plumbing, electrical &amp; property improvements</li>
          </ul>
          <span className="inline-flex items-center text-brand-navy font-bold text-base lg:text-lg group-hover:text-brand-yellow transition-colors">
            Homeowner Services <ChevronRight className="w-5 h-5 ml-1" />
          </span>
        </Link>
      </div>
    </div>
  </section>
);

// ============ SIMPLIFY / PROFESSIONAL MAINTENANCE SECTION ============
const SimplifySection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-grey">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4 sm:mb-6">
        Simplify Your Property Maintenance
      </h2>
      <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate max-w-3xl mx-auto leading-relaxed mb-8 sm:mb-10">
        Managing repairs across a property or portfolio can involve multiple trades, appointments and contractors.
        Expert Property Maintenance provides one point of contact to coordinate the work, helping landlords and
        property professionals reduce administration and keep work moving.
      </p>
      <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3 mb-8 sm:mb-10">
        {[
          'Reactive Repairs',
          'Planned Maintenance',
          'Tenant-Reported Problems',
          'End-of-Tenancy Works',
          'Void Property Works',
          'Refurbishments'
        ].map((item) => (
          <span key={item} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-brand-navy rounded-full text-[15px] sm:text-base font-medium border border-slate-200">
            <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
            {item}
          </span>
        ))}
      </div>
      <Button size="lg" className="bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold w-full sm:w-auto text-base lg:text-lg px-8 h-12 lg:h-14" asChild data-testid="simplify-cta">
        <a href="#contact">
          Discuss Your Maintenance Requirements
          <ArrowRight className="w-5 h-5 ml-2" />
        </a>
      </Button>
    </div>
  </section>
);

// ============ CORE SERVICES SECTION ============
const CoreServicesSection = () => (
  <section id="services" className="py-14 sm:py-18 lg:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          Property Maintenance Services
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate leading-relaxed">
          One point of contact for landlords, letting agents and homeowners. From a single repair to
          larger projects, we coordinate every trade so you don't have to organise multiple contractors.
          These general services are available for both private homes and professionally managed or rental properties.
        </p>
      </div>
      
      {/* Single column on mobile, 2 on tablet, 3 on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
        {coreServices.slice(0, 9).map((service) => {
          const Icon = iconMap[service.icon] || Wrench;
          return (
            <Card key={service.id} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all group h-full">
              <CardContent className="p-6 lg:p-8">
                <div className="flex items-start gap-4 sm:block">
                  <div className="w-14 h-14 lg:w-16 lg:h-16 bg-brand-grey rounded-lg flex items-center justify-center flex-shrink-0 sm:mb-5 group-hover:bg-brand-yellow transition-colors">
                    <Icon className="w-7 h-7 lg:w-8 lg:h-8 text-brand-yellow group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h3 className="text-[17px] sm:text-lg lg:text-[19px] font-bold text-brand-navy mb-2">{service.title}</h3>
                    <p className="text-brand-slate text-[15px] sm:text-base lg:text-[16px] leading-relaxed">{service.description}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      <div className="text-center mt-12 sm:mt-14">
        <Button size="lg" className="bg-brand-navy hover:bg-brand-navy/90 text-white font-semibold w-full sm:w-auto text-base lg:text-lg px-8 h-12 lg:h-14" asChild>
          <a href="#contact">
            Discuss Your Requirements
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
      </div>
    </div>
  </section>
);

// ============ LANDLORDS & AGENTS SECTION ============
const LandlordsSection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-grey">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
        <div>
          <div className="inline-flex items-center gap-2 bg-white text-yellow-800 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-sm font-medium mb-4 sm:mb-6">
            <Key className="w-4 h-4" />
            For Property Professionals
          </div>
          
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4 sm:mb-6">
            A Practical Maintenance Partner for Landlords & Property Professionals
          </h2>
          
          <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate mb-4 sm:mb-5 leading-relaxed">
            Whether you manage one property or a larger portfolio, we provide a straightforward way to arrange
            repairs, maintenance and refurbishment without coordinating multiple contractors yourself.
          </p>

          <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate mb-4 sm:mb-5 leading-relaxed">
            Send us the requirement, property details and access information. We assess the work, coordinate the
            appropriate trades and remain your main point of contact.
          </p>

          <p className="text-[15px] sm:text-base lg:text-[16px] text-brand-slate mb-6 sm:mb-8 leading-relaxed border-l-2 border-brand-yellow pl-4">
            With previous experience working within an estate agency environment, we understand the importance of
            reliable contractors, clear communication, tenant access and keeping property work progressing.
          </p>
          
          {/* Customer types we work with */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[
              'Letting Agents',
              'Estate Agents',
              'Landlords',
              'Property Managers',
              'Portfolio Owners',
              'Property Investors'
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                <span className="font-medium text-brand-navy text-[15px] sm:text-base lg:text-[17px]">{item}</span>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold w-full sm:w-auto text-base" asChild>
              <a href="#contact">
                Discuss Your Properties
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto text-base" asChild>
              <a href="/landlord-services">
                Learn More
                <ChevronRight className="w-5 h-5 ml-1" />
              </a>
            </Button>
          </div>
        </div>
        
        {/* Right-side card - reduced to 6 key items */}
        <div className="hidden lg:block bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
          <h3 className="text-xl font-bold text-brand-navy mb-6">We Can Help With:</h3>
          <div className="space-y-4">
            {[
              { title: "Reactive Repairs", description: "Fast response to urgent issues and tenant-reported problems." },
              { title: "Ongoing Maintenance", description: "Regular support for landlords managing multiple properties." },
              { title: "Tenant-Reported Problems", description: "Prompt response keeping you informed throughout." },
              { title: "End of Tenancy Repairs", description: "Quick turnaround to minimise void periods." },
              { title: "Void Property Works", description: "Complete preparation including repairs and decorating." },
              { title: "Full Refurbishments", description: "Multi-trade renovations to maximise rental potential." }
            ].map((service) => (
              <div key={service.title} className="flex items-start gap-3 pb-4 border-b border-slate-100 last:border-0 last:pb-0">
                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-brand-slate" />
                </div>
                <div>
                  <div className="font-semibold text-brand-navy text-[16px]">{service.title}</div>
                  <div className="text-[15px] text-brand-slate leading-relaxed">{service.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============ SPECIALIST BRANDS SECTION ============
const SpecialistBrandsSection = () => (
  <section id="about" className="py-14 sm:py-18 lg:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          Established Specialist Brands
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate leading-relaxed">
          Expert Property Maintenance brings together established specialist trade services 
          under one property maintenance company. Each brand represents expertise in its field.
        </p>
      </div>
      
      {/* 4-card grid: 1 col mobile, 2 tablet, 4 on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
        {specialistBrands.map((brand) => (
          <Card key={brand.id} className="border-slate-200 hover:shadow-lg transition-all overflow-hidden group h-full">
            <CardContent className="p-5 lg:p-6 h-full flex flex-col items-center text-center">
              {/* Logo shown directly on the white card, consistent area, aspect preserved */}
              <div className="flex items-center justify-center w-full h-[96px] sm:h-[110px] lg:h-[124px] mb-4 sm:mb-5">
                <img
                  src={brand.logo}
                  alt={brand.name}
                  loading="lazy"
                  className="max-h-full w-auto max-w-[200px] lg:max-w-[210px] object-contain transition-transform duration-300 group-hover:scale-[1.04]"
                />
              </div>
              <h3 className="font-bold text-brand-navy text-[17px] lg:text-lg mb-1">{brand.name}</h3>
              <p className="text-sm text-brand-slate mb-3">{brand.tagline}</p>
              <p className="text-brand-slate text-[15px] leading-relaxed mb-5 hidden sm:block">{brand.description}</p>
              <a
                href={brand.website}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Visit ${brand.name} website`}
                className="mt-auto inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold text-brand-navy hover:bg-brand-grey transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2"
                data-testid={`brand-visit-${brand.id}`}
              >
                Visit Site
                <ExternalLink className="w-4 h-4 text-brand-yellow" />
              </a>
            </CardContent>
          </Card>
        ))}
      </div>
      
      <div className="mt-12 sm:mt-16 bg-brand-navy rounded-2xl p-6 sm:p-8 lg:p-10 text-center">
        <p className="text-slate-300 text-base sm:text-lg lg:text-[17px] mb-3 sm:mb-4">
          All specialist services available through one point of contact.
        </p>
        <p className="text-white text-lg sm:text-xl lg:text-2xl font-bold">
          Call {companyInfo.phone} or request a quote below.
        </p>
      </div>
    </div>
  </section>
);

// ============ REFURBISHMENT SECTION ============
const RefurbishmentSection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-navy text-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
            Property Refurbishment
          </h2>
          <p className="text-base sm:text-lg lg:text-[17px] text-slate-300 mb-4 leading-relaxed">
            Whether you are preparing a property for new tenants, updating a tired rental, 
            or completing a full renovation, we handle refurbishment projects of all sizes.
          </p>
          <p className="text-base sm:text-lg lg:text-[17px] text-slate-300 mb-6 leading-relaxed">
            For homeowners too, we take on single-room improvements, larger home renovations and full property refurbishments.
          </p>
          
          {/* Single column on mobile, 2 on tablet+ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[
              'Rental property refurbishments',
              'Properties between tenancies',
              'Tired or damaged properties',
              'Decorating & flooring',
              'Carpentry & joinery',
              'Kitchens & bathrooms',
              'Plaster repairs',
              'General repairs',
              'Preparation for reletting'
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                <span className="text-slate-300 text-[15px] sm:text-base lg:text-[16px]">{item}</span>
              </div>
            ))}
          </div>
          
          <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold w-full sm:w-auto text-base" asChild>
            <a href="/property-refurbishment">
              Property Refurbishment
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
        </div>
        
        <div className="bg-slate-800 rounded-2xl p-5 sm:p-8">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-4 sm:mb-6">Typical Refurbishment Projects</h3>
          <div className="space-y-3 sm:space-y-4">
            {[
              {
                title: 'Void Property Turnaround',
                desc: 'Complete refresh including repairs, redecoration, flooring and deep clean.'
              },
              {
                title: 'Buy-to-Let Renovation',
                desc: 'Full property update to maximise rental value and attract quality tenants.'
              },
              {
                title: 'End of Tenancy Refresh',
                desc: 'Quick turnaround repairs and decorating between tenancies.'
              }
            ].map((project) => (
              <div key={project.title} className="p-4 bg-slate-700/50 rounded-xl">
                <h4 className="font-semibold text-white mb-1.5 text-base lg:text-[17px]">{project.title}</h4>
                <p className="text-[15px] lg:text-base text-slate-400 leading-relaxed">{project.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ============ HOW IT WORKS SECTION ============
const HowItWorksSection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-grey">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          How It Works
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate">
          Getting your property work done is straightforward — whether it is a single repair or a full refurbishment.
        </p>
      </div>
      
      {/* Vertical stack on mobile, 2x2 on tablet, 4 across on desktop with connecting line */}
      <div className="relative">
        {/* Connecting line on desktop only */}
        <div className="hidden lg:block absolute top-14 left-[12%] right-[12%] h-0.5 bg-brand-yellow/30" />
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {howItWorks.map((step) => (
            <div key={step.step} className="flex sm:flex-col items-start sm:items-center text-left sm:text-center gap-5 sm:gap-0 relative">
              <div className="w-18 h-18 sm:w-24 sm:h-24 lg:w-28 lg:h-28 bg-brand-yellow text-brand-navy rounded-full flex items-center justify-center text-2xl sm:text-3xl lg:text-4xl font-bold flex-shrink-0 sm:mx-auto sm:mb-6 relative z-10">
                {step.step}
              </div>
              <div>
                <h3 className="text-lg sm:text-xl lg:text-[19px] font-bold text-brand-navy mb-2 sm:mb-3">{step.title}</h3>
                <p className="text-brand-slate text-[15px] sm:text-base lg:text-[16px] leading-relaxed max-w-xs mx-auto">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ============ WHY CHOOSE US SECTION ============
const WhyChooseSection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          Why Expert Property Maintenance
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate leading-relaxed">
          A reliable multi-trade partner that landlords, letting agents, property professionals and homeowners can all use with confidence.
        </p>
      </div>
      
      {/* Single column on mobile, 2 on tablet, 3 on desktop */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-7">
        {whyChooseUs.map((item) => (
          <Card key={item.title} className="border-slate-200 h-full">
            <CardContent className="p-6 lg:p-8">
              <div className="flex items-start gap-4 sm:block">
                <div className="w-14 h-14 lg:w-16 lg:h-16 bg-brand-navy rounded-lg flex items-center justify-center flex-shrink-0 sm:mb-5">
                  <CheckCircle2 className="w-7 h-7 lg:w-8 lg:h-8 text-brand-yellow" />
                </div>
                <div>
                  <h3 className="text-[17px] sm:text-lg lg:text-[19px] font-bold text-brand-navy mb-2 sm:mb-3">{item.title}</h3>
                  <p className="text-brand-slate text-[15px] sm:text-base lg:text-[16px] leading-relaxed">{item.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

// ============ TESTIMONIALS SECTION ============
// Testimonials section hidden until genuine customer reviews are supplied
// Do not display mock/placeholder testimonials on production
const TestimonialsSection = () => null;

// ============ HOMEOWNERS SECTION ============
const HomeownersSection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-grey">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <div className="inline-flex items-center gap-2 bg-white text-brand-navy px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-sm font-medium mb-4 sm:mb-6">
        <HomeIcon className="w-4 h-4" />
        For Homeowners
      </div>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4 sm:mb-6">
        Property Maintenance for Homeowners
      </h2>
      <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate max-w-2xl mx-auto leading-relaxed mb-6 sm:mb-8">
        Our multi-trade service is also available to homeowners looking for a reliable contractor for repairs,
        decorating, carpentry, plastering and larger property improvement work.
      </p>
      <div className="flex flex-wrap justify-center gap-2.5 sm:gap-3 mb-8">
        {[
          'Painting & decorating',
          'Carpentry & joinery',
          'Doors, skirting & architraves',
          'Plaster repairs',
          'Shelving & storage',
          'Minor plumbing',
          'Minor electrical',
          'Property improvements'
        ].map((item) => (
          <span key={item} className="inline-flex items-center gap-2 px-3.5 py-2 bg-white text-brand-navy rounded-full text-[14px] sm:text-[15px] font-medium border border-slate-200">
            <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
            {item}
          </span>
        ))}
      </div>
      <Button size="lg" variant="outline" className="w-full sm:w-auto text-base" asChild data-testid="homeowners-cta">
        <a href="/homeowner-services">
          Homeowner Services
          <ChevronRight className="w-5 h-5 ml-1" />
        </a>
      </Button>
    </div>
  </section>
);

// ============ SERVICE AREAS SECTION ============
const ServiceAreasSection = () => (
  <section id="areas" className="py-14 sm:py-18 lg:py-24 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy mb-4">
          Areas We Cover
        </h2>
        <p className="text-base sm:text-lg lg:text-[17px] text-brand-slate">
          Serving landlords, letting agents and homeowners across Greater Manchester, Cheshire and surrounding areas.
        </p>
      </div>
      
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
        {serviceAreas.map((area) => (
          <span 
            key={area}
            className="px-4 py-2.5 sm:px-6 sm:py-3 bg-slate-100 text-brand-slate rounded-full text-[15px] sm:text-base lg:text-[16px] font-medium hover:bg-brand-grey transition-colors"
          >
            {area}
          </span>
        ))}
      </div>
    </div>
  </section>
);

// ============ FINAL CTA SECTION ============
const FinalCTASection = () => (
  <section className="py-14 sm:py-18 lg:py-24 bg-brand-navy">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
        One Contact for Your Property Maintenance
      </h2>
      <p className="text-base sm:text-lg lg:text-[18px] text-slate-300 mb-8 leading-relaxed">
        Supporting landlords, letting agents, property managers and homeowners with repairs, 
        maintenance and refurbishment across Greater Manchester & Cheshire.
      </p>
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 justify-center">
        <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg w-full sm:w-auto" asChild>
          <a href="#contact">
            Request a Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
        <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy font-semibold px-6 sm:px-8 h-12 sm:h-14 text-base sm:text-lg w-full sm:w-auto" asChild>
          <a href={`tel:${companyInfo.phone}`}>
            <Phone className="w-5 h-5 mr-2" />
            {companyInfo.phone}
          </a>
        </Button>
      </div>
    </div>
  </section>
);

// ============ CONTACT SECTION ============
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    customer_type: '',
    job_description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.postcode,
          job_description: `[${formData.customer_type || 'Not specified'}] ${formData.job_description}`,
          trade_type: 'general',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/'
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', postcode: '', customer_type: '', job_description: '' });
      } else if (response.status === 429) {
        let detail = '';
        try { detail = (await response.json()).detail; } catch {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Could not parse 429 response body; using default message.');
          }
        }
        setError(detail || 'Too many requests. Please wait a moment and try again, or call us directly.');
      } else {
        setError('Something went wrong. Please try again or call us directly.');
      }
    } catch {
      setError('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-14 sm:py-18 lg:py-24 bg-brand-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          <div className="text-white">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6">
              Request a Quote
            </h2>
            <p className="text-slate-300 text-base sm:text-lg lg:text-[17px] mb-6 sm:mb-8 leading-relaxed">
              Tell us about your property maintenance needs. We aim to respond promptly.
            </p>
            
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-brand-navy" />
                </div>
                <div>
                  <div className="text-slate-400 text-sm">Call us</div>
                  <a href={`tel:${companyInfo.phone}`} className="text-white font-bold text-lg hover:text-brand-yellow">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-brand-navy" />
                </div>
                <div>
                  <div className="text-slate-400 text-sm">Email us</div>
                  <a href={`mailto:${companyInfo.email}`} className="text-white font-bold hover:text-brand-yellow text-sm sm:text-base break-all">
                    {companyInfo.email}
                  </a>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-brand-navy" />
                </div>
                <div>
                  <div className="text-slate-400 text-sm">Service area</div>
                  <div className="text-white font-bold">{companyInfo.serviceArea}</div>
                </div>
              </div>
            </div>
            
            <div className="mt-6 sm:mt-8 p-4 bg-slate-800 rounded-xl">
              <div className="text-sm text-slate-400 mb-2">Insurance & Guarantee</div>
              <div className="text-white font-medium text-[15px] sm:text-base">{companyInfo.insurance.publicLiability}</div>
              <div className="text-white font-medium text-[15px] sm:text-base">{companyInfo.guarantee}</div>
            </div>
          </div>

          <Card className="bg-white border-0 shadow-2xl">
            <CardContent className="p-5 sm:p-6 lg:p-8">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-brand-navy mb-2">Quote Request Received!</h3>
                  <p className="text-brand-slate">We aim to respond promptly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-testid="contact-form">
                  <h3 className="text-lg sm:text-xl lg:text-[22px] font-bold text-brand-navy mb-2 sm:mb-4">Request a Quote</h3>
                  
                  {/* Name - always full width */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                    <Input 
                      required 
                      value={formData.name} 
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                      placeholder="John Smith" 
                      className="h-12 text-base"
                      data-testid="input-name"
                    />
                  </div>

                  {/* Email - full width on mobile */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                    <Input 
                      type="email" 
                      required 
                      value={formData.email} 
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                      placeholder="john@email.com" 
                      className="h-12 text-base"
                      data-testid="input-email"
                    />
                  </div>
                  
                  {/* Phone - full width on mobile */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                    <Input 
                      type="tel" 
                      required 
                      value={formData.phone} 
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })} 
                      placeholder="07xxx xxxxxx" 
                      className="h-12 text-base"
                      data-testid="input-phone"
                    />
                  </div>

                  {/* Postcode - full width on mobile */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">Postcode / Area</label>
                    <Input 
                      value={formData.postcode} 
                      onChange={(e) => setFormData({ ...formData, postcode: e.target.value })} 
                      placeholder="e.g., M1 or Stockport" 
                      className="h-12 text-base"
                      data-testid="input-postcode"
                    />
                  </div>
                  
                  {/* Customer type - full width on mobile */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">I am a:</label>
                    <Select value={formData.customer_type} onValueChange={(value) => setFormData({ ...formData, customer_type: value })}>
                      <SelectTrigger className="h-12 text-base" data-testid="select-customer-type">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {customerTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Job description - always full width */}
                  <div>
                    <label className="block text-sm font-medium text-brand-slate mb-1.5">What do you need help with? *</label>
                    <Textarea 
                      required 
                      value={formData.job_description} 
                      onChange={(e) => setFormData({ ...formData, job_description: e.target.value })} 
                      placeholder="Describe the work you need..." 
                      rows={4}
                      className="text-base"
                      data-testid="textarea-description"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 sm:h-14 text-base sm:text-lg" 
                    disabled={isSubmitting}
                    data-testid="submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : (
                      <>Request Quote <ArrowRight className="w-5 h-5 ml-2" /></>
                    )}
                  </Button>

                  <p className="text-sm text-brand-slate/80 text-center">
                    We aim to respond promptly. No obligation.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// ============ FOOTER ============
const Footer = () => (
  <footer className="bg-slate-950 text-slate-400 py-12 lg:py-16">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Mobile Footer CTA - visible only on mobile, above content */}
      <div className="md:hidden mb-8 pb-8 border-b border-slate-800">
        <p className="text-white font-semibold text-lg mb-3 text-center">Ready to get started?</p>
        <Button 
          size="lg" 
          className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold text-base h-12"
          asChild
        >
          <a href="#contact">
            Request a Quote
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-8 lg:gap-12 mb-8 lg:mb-12">
        <div className="md:col-span-2">
          {/* Logo in white container for visibility on dark background */}
          <div className="bg-white rounded-lg p-3 inline-block mb-4">
            <img src={MASTER_LOGO_URL} alt="Expert Property Maintenance" className="h-10 lg:h-12 w-auto" />
          </div>
          <p className="text-slate-300 font-medium text-base lg:text-[17px] mb-1">Property Maintenance for Landlords, Letting Agents & Homeowners</p>
          <p className="text-slate-500 text-[15px] lg:text-base">Serving Greater Manchester & Cheshire</p>
          <div className="mt-5 text-[15px] lg:text-base text-slate-500">
            <p>{companyInfo.insurance.publicLiability}</p>
            <p>{companyInfo.guarantee}</p>
          </div>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-base lg:text-[17px]">Services</h4>
          <ul className="space-y-2.5 text-[15px] lg:text-base">
            <li><a href="#services" className="hover:text-brand-yellow transition-colors">Property Maintenance</a></li>
            <li><a href="/landlord-services" className="hover:text-brand-yellow transition-colors">Landlord Services</a></li>
            <li><a href="/homeowner-services" className="hover:text-brand-yellow transition-colors">Homeowner Services</a></li>
            <li><a href="/property-refurbishment" className="hover:text-brand-yellow transition-colors">Refurbishment</a></li>
            <li><a href="/join" className="hover:text-brand-yellow transition-colors">Join Our Team</a></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-base lg:text-[17px]">Areas We Serve</h4>
          <ul className="space-y-2.5 text-[15px] lg:text-base columns-2 gap-x-6">
            <li><a href="/property-maintenance-manchester" className="hover:text-brand-yellow transition-colors">Manchester</a></li>
            <li><a href="/property-maintenance-stockport" className="hover:text-brand-yellow transition-colors">Stockport</a></li>
            <li><a href="/property-maintenance-cheadle" className="hover:text-brand-yellow transition-colors">Cheadle</a></li>
            <li><a href="/property-maintenance-didsbury" className="hover:text-brand-yellow transition-colors">Didsbury</a></li>
            <li><a href="/property-maintenance-sale" className="hover:text-brand-yellow transition-colors">Sale</a></li>
            <li><a href="/property-maintenance-altrincham" className="hover:text-brand-yellow transition-colors">Altrincham</a></li>
            <li><a href="/property-maintenance-salford" className="hover:text-brand-yellow transition-colors">Salford</a></li>
            <li><span className="hover:text-brand-yellow transition-colors">Trafford</span></li>
            <li><span className="hover:text-brand-yellow transition-colors">Oldham</span></li>
            <li><span className="hover:text-brand-yellow transition-colors">Warrington</span></li>
            <li><a href="/property-maintenance-wilmslow" className="hover:text-brand-yellow transition-colors">Wilmslow</a></li>
            <li><span className="hover:text-brand-yellow transition-colors">Macclesfield</span></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
        <p className="text-[15px] lg:text-base text-slate-500">
          © {new Date().getFullYear()} Expert Property Maintenance
          <Link
            to="/admin-login"
            className="text-slate-700 hover:text-slate-500 transition-colors ml-2"
            aria-label="Staff login"
            data-testid="footer-admin-link"
          >
            ·
          </Link>
        </p>
        <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-brand-yellow hover:text-yellow-300 text-base font-medium">
          <Phone className="w-4 h-4" />
          {companyInfo.phone}
        </a>
      </div>
    </div>
  </footer>
);

// ============ MAIN HOME COMPONENT ============
const Home = () => {
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(scrollTo);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Property Maintenance for Landlords, Letting Agents & Homeowners | Greater Manchester</title>
        <meta name="description" content="Expert Property Maintenance - Reliable repairs, maintenance and refurbishment for landlords, letting agents and homeowners across Greater Manchester & Cheshire." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/" />
        <meta property="og:title" content="Expert Property Maintenance | Landlords, Letting Agents & Homeowners" />
        <meta property="og:description" content="Property maintenance for landlords, letting agents and homeowners. Repairs, void works, refurbishments across Greater Manchester & Cheshire." />
        <meta property="og:url" content="https://expert-propertymaintenance.co.uk/" />
      </Helmet>
      <Header variant="default" companyPhone={companyInfo.phone} />
      <main>
        <HeroSection />
        <CustomerPathwaySection />
        <SimplifySection />
        <CoreServicesSection />
        <LandlordsSection />
        <SpecialistBrandsSection />
        <RefurbishmentSection />
        <HowItWorksSection />
        <WhyChooseSection />
        <TestimonialsSection />
        <HomeownersSection />
        <ServiceAreasSection />
        <FinalCTASection />
        <ContactSection />
      </main>
      <Footer />
      <MobileActionBar phone={companyInfo.phone} />
    </div>
  );
};

export default Home;
